import { autoPlacement, autoUpdate, computePosition, flip, offset, shift, type Placement } from "@floating-ui/dom"
import type Alpine from "alpinejs"
import { debounce, memoize } from "lodash-es"

const PANEL_OFFSET = 4
const VIEWPORT_PAD = 8
const MORE_PANEL_COL_SIZE = 10
const ADMIN_NAV = "ADMIN_NAV"
const ONE_YEAR_SECONDS = 31_536_000

const getRootFontSize = memoize((): number => parseFloat(getComputedStyle(document.documentElement).fontSize))

/** Returns the `sm` breakpoint in pixels, read once from the Tailwind CSS variable `--breakpoint-sm`. */
const getMobileBreakpoint = memoize((): number => {
  return parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--breakpoint-sm")) * getRootFontSize()
})

/**
 * Persists the current nav overflow state to a cookie so the server can
 * pre-render the correct number of visible items on the next page load,
 * preventing a flash of collapsed/expanded content before Alpine initialises.
 *
 * The cookie value is a JSON array `[totalCount, visibleCount]`. The server
 * validates `totalCount` against the live menu length before trusting
 * `visibleCount` — if the menu has changed since the cookie was written, the
 * server falls back to rendering all items visible and lets Alpine recompute
 * on the client.
 *
 * @param totalCount - Total number of top-level nav items (used as a version check on the server).
 * @param visibleCount - Number of items that currently fit in the available width.
 */
function saveNavCookie(totalCount: number, visibleCount: number): void {
  try {
    document.cookie = `${ADMIN_NAV}=${JSON.stringify([totalCount, visibleCount])}; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Strict`
  } catch {
    // ignore
  }
}

const saveNavCookieDebounced = debounce(saveNavCookie, 200)

type AdminNavComponent = {
  $el: HTMLElement
  $nextTick: (callback?: () => void) => Promise<void>
  $watch<T>(property: string, callback: (newVal: T, oldVal: T) => void): void
  totalCount: number
  visibleCount: number
  openDropdown: string | null
  openSubmenu: string | null
  openMobile: boolean
  openMobileSection: string | null
  openMobileUser: boolean
  observer: ResizeObserver | null
  panelCleanups: Map<string, () => void>
  init(this: AdminNavComponent): void
  destroy(this: AdminNavComponent): void
  recompute(this: AdminNavComponent): void
  toggleDropdown(this: AdminNavComponent, id: string): void
  toggleSubmenu(this: AdminNavComponent, id: string): void
  toggleAvatar(this: AdminNavComponent): void
  positionPanel(this: AdminNavComponent, id: string): void
  positionSubmenu(this: AdminNavComponent, id: string): void
}

/**
 * Registers the `adminNav` Alpine.js component.
 *
 * Measures top-level nav item widths against the available container space and
 * collapses items that do not fit into a "More" overflow dropdown. A
 * ResizeObserver re-runs the computation whenever the container resizes.
 * Dropdown and submenu panels are positioned via @floating-ui/dom using
 * autoPlacement so each panel lands on whichever side has the most space.
 *
 * @param alpine - The Alpine.js instance, received from the entrypoint plugin registration.
 */
export function adminNav(alpine: typeof Alpine): void {
  alpine.data(
    "adminNav",
    /**
     * @param totalCount - Total number of top-level nav items.
     * @param initialVisibleCount - Server-provided initial value from the ADMIN_NAV cookie.
     */
    (
      totalCount: number = 0,
      initialVisibleCount: number = totalCount
    ): Omit<AdminNavComponent, "$el" | "$nextTick" | "$watch"> => ({
      totalCount,
      visibleCount: initialVisibleCount,
      openDropdown: null,
      openSubmenu: null,
      openMobile: false,
      openMobileSection: null,
      openMobileUser: false,
      observer: null,
      panelCleanups: new Map(),

      /** Verifies the server-provided visibleCount with a live measurement and keeps the cookie fresh. */
      init(this: AdminNavComponent) {
        requestAnimationFrame(() => {
          const prevCount = this.visibleCount
          this.recompute()
          saveNavCookie(this.totalCount, this.visibleCount)

          const container = this.$el.querySelector<HTMLElement>("[data-items-container]")

          const setupWatchers = () => {
            this.observer = new ResizeObserver(() => {
              this.recompute()
              saveNavCookieDebounced(this.totalCount, this.visibleCount)
              if (
                window.innerWidth < getMobileBreakpoint() &&
                (this.openDropdown !== null || this.openSubmenu !== null)
              ) {
                this.openDropdown = null
                this.openSubmenu = null
              }
            })
            this.observer.observe(this.$el)
            this.$watch<string | null>("openDropdown", (newVal, oldVal) => {
              if (oldVal && oldVal !== newVal) {
                this.panelCleanups.get(oldVal)?.()
                this.panelCleanups.delete(oldVal)
                // Stop the submenu's autoUpdate immediately so the More panel's
                // leave animation (scale transform) can't trigger a reposition
                // that sends the fixed-position submenu panel off-screen.
                if (this.openSubmenu) {
                  this.panelCleanups.get(this.openSubmenu)?.()
                  this.panelCleanups.delete(this.openSubmenu)
                  const root = this.$el.closest<HTMLElement>("[x-data]") ?? this.$el
                  const submenuPanel = root.querySelector<HTMLElement>(`[data-submenu-panel="${this.openSubmenu}"]`)
                  if (submenuPanel) submenuPanel.style.visibility = "hidden"
                }
              }
            })
            this.$watch<string | null>("openSubmenu", (newVal, oldVal) => {
              if (oldVal && oldVal !== newVal) {
                this.panelCleanups.get(oldVal)?.()
                this.panelCleanups.delete(oldVal)
                const root = this.$el.closest<HTMLElement>("[x-data]") ?? this.$el
                const submenuPanel = root.querySelector<HTMLElement>(`[data-submenu-panel="${oldVal}"]`)
                if (submenuPanel) submenuPanel.style.visibility = "hidden"
              }
            })
            this.$watch<boolean>("openMobile", (val) => {
              document.body.classList.toggle("admin-nav-mobile-open", val)
            })
          }

          if (this.visibleCount === prevCount) {
            // visibleCount unchanged — server-rendered state is already correct,
            // no reactive x-show updates to wait for, reveal immediately.
            container?.style.removeProperty("visibility")
            setupWatchers()
          } else {
            // visibleCount changed — wait for Alpine to apply x-show updates before revealing.
            this.$nextTick(() => {
              container?.style.removeProperty("visibility")
              setupWatchers()
            })
          }
        })
      },

      destroy(this: AdminNavComponent) {
        this.observer?.disconnect()
        this.panelCleanups.forEach((cleanup) => cleanup())
        this.panelCleanups.clear()
        document.body.classList.remove("admin-nav-mobile-open")
      },

      /** Recalculates how many items fit in the available width. */
      recompute(this: AdminNavComponent) {
        const container = this.$el.querySelector("[data-items-container]") as HTMLElement | null
        const measure = this.$el.querySelector("[data-measure]") as HTMLElement | null
        if (!container || !measure) return
        if (container.clientWidth === 0) return

        const gap = parseFloat(getComputedStyle(measure).gap) || 0
        const moreEl = measure.querySelector<HTMLElement>("[data-measure-more]")
        const moreWidth = moreEl?.offsetWidth ?? 0

        const available = container.clientWidth
        const items = Array.from(measure.querySelectorAll<HTMLElement>(":scope > :not([data-measure-more])"))
        if (items.length === 0) return

        const widths = items.map((el) => el.offsetWidth)
        const totalWidth = widths.reduce((sum, w, i) => sum + w + (i > 0 ? gap : 0), 0)

        if (totalWidth <= available) {
          this.visibleCount = items.length
          return
        }

        const budget = available - moreWidth - gap
        let used = 0
        let count = 0
        for (let i = 0; i < widths.length; i++) {
          const w = widths[i] + (i > 0 ? gap : 0)
          if (used + w > budget) break
          used += w
          count++
        }
        this.visibleCount = Math.max(0, count)
      },

      /** Opens the named top-level dropdown (or closes it if already open) and positions it. */
      toggleDropdown(this: AdminNavComponent, id: string) {
        if (this.openDropdown === id) {
          this.openDropdown = null
          this.openSubmenu = null
        } else {
          // Hide before Alpine removes display:none so the panel never flashes at the wrong position.
          const root = this.$el.closest<HTMLElement>("[x-data]") ?? this.$el
          const panel = root.querySelector<HTMLElement>(`[data-dropdown-panel="${id}"]`)
          if (panel) panel.style.visibility = "hidden"
          this.openDropdown = id
          this.openSubmenu = null
          this.$nextTick(() => this.positionPanel(id))
        }
      },

      /**
       * On mobile-width screens, shows/hides a standalone full-width user panel and
       * closes the hamburger panel. On desktop, delegates to toggleDropdown.
       */
      toggleAvatar(this: AdminNavComponent) {
        if (window.innerWidth < getMobileBreakpoint()) {
          this.openDropdown = null
          this.openMobile = false
          this.openMobileUser = !this.openMobileUser
        } else {
          this.toggleDropdown("__user__")
        }
      },

      /** Opens the named overflow submenu (or closes it if already open) and positions it. */
      toggleSubmenu(this: AdminNavComponent, id: string) {
        if (this.openSubmenu === id) {
          this.openSubmenu = null
        } else {
          const root = this.$el.closest<HTMLElement>("[x-data]") ?? this.$el
          const panel = root.querySelector<HTMLElement>(`[data-submenu-panel="${id}"]`)
          if (panel) panel.style.visibility = "hidden"
          this.openSubmenu = id
          this.$nextTick(() => this.positionSubmenu(id))
        }
      },

      /**
       * Positions a top-level dropdown panel using autoPlacement, which picks
       * whichever placement has the most available space. This means right-side
       * items (e.g. "Ignore") automatically land on bottom-end while left-side
       * items land on bottom-start — no manual flip logic needed.
       *
       * The panel is hidden while it renders so multi-column layout widths are
       * fully computed before Floating UI measures the element.
       */
      positionPanel(this: AdminNavComponent, id: string) {
        const root = this.$el.closest<HTMLElement>("[x-data]") ?? this.$el
        const trigger = root.querySelector<HTMLElement>(`[data-dropdown-trigger="${id}"]`)
        const panel = root.querySelector<HTMLElement>(`[data-dropdown-panel="${id}"]`)
        if (!trigger || !panel) return

        if (id === "__more__") {
          // The More panel's column count depends on the live overflow count, so it
          // can't be rendered server-side like the other panels' static `grid` class —
          // toggle the same `grid` class here instead of manipulating style.display.
          const moreCount = this.totalCount - this.visibleCount
          const colCount = Math.max(1, Math.ceil(moreCount / MORE_PANEL_COL_SIZE))
          if (colCount > 1) {
            panel.classList.add("grid")
            panel.style.gridTemplateColumns = `repeat(${colCount}, max-content)`
          } else {
            panel.classList.remove("grid")
            panel.style.removeProperty("grid-template-columns")
          }
        }

        panel.style.visibility = "hidden"

        // __more__ and __user__ are always on the right side of the nav bar;
        // restrict their allowed placements to end-aligned options only.
        const allowedPlacements =
          id === "__more__" || id === "__user__"
            ? (["bottom-end", "top-end"] as Placement[])
            : (["bottom-start", "bottom-end", "top-start", "top-end"] as Placement[])

        let callToken = 0
        const update = () => {
          const myToken = ++callToken
          computePosition(trigger, panel, {
            strategy: "fixed",
            middleware: [
              offset(PANEL_OFFSET),
              autoPlacement({ allowedPlacements, crossAxis: true, padding: VIEWPORT_PAD }),
              shift({ padding: VIEWPORT_PAD }),
            ],
          }).then(({ x, y }) => {
            if (myToken !== callToken) return
            if (this.openDropdown !== id) return
            Object.assign(panel.style, { position: "fixed", left: `${x}px`, top: `${y}px`, visibility: "" })
          })
        }

        requestAnimationFrame(() => {
          if (this.openDropdown !== id) return
          this.panelCleanups.set(id, autoUpdate(trigger, panel, update))
        })
      },

      /**
       * Positions an overflow submenu beside its trigger, preferring a left
       * flyout (the More panel sits on the right) and flipping to the right when
       * there is no room. When neither side fits — e.g. a wide multi-column
       * submenu on a narrow window — it falls back to opening below or above the
       * trigger, including bottom-centre, so every option stays reachable.
       */
      positionSubmenu(this: AdminNavComponent, id: string) {
        const root = this.$el.closest<HTMLElement>("[x-data]") ?? this.$el
        const trigger = root.querySelector<HTMLElement>(`[data-submenu-trigger="${id}"]`)
        const panel = root.querySelector<HTMLElement>(`[data-submenu-panel="${id}"]`)
        if (!trigger || !panel) return

        panel.style.visibility = "hidden"

        let callToken = 0
        const update = () => {
          const myToken = ++callToken
          computePosition(trigger, panel, {
            strategy: "fixed",
            placement: "left-start",
            middleware: [
              offset(PANEL_OFFSET),
              flip({
                fallbackPlacements: ["right-start", "bottom-end", "bottom", "bottom-start", "top-end", "top"],
                padding: VIEWPORT_PAD,
              }),
              shift({ padding: VIEWPORT_PAD }),
            ],
          }).then(({ x, y }) => {
            if (myToken !== callToken) return
            if (this.openSubmenu !== id) return
            Object.assign(panel.style, { position: "fixed", left: `${x}px`, top: `${y}px`, visibility: "" })
          })
        }

        requestAnimationFrame(() => {
          if (this.openSubmenu !== id) return
          this.panelCleanups.set(id, autoUpdate(trigger, panel, update))
        })
      },
    })
  )
}
